import { GroceryItem, MealPlan, getAssignmentMealType } from '../types/mealPlan';
import { formatQuantity } from './formatQuantity';
import { formatDateRange } from './mealPlanUtils';
import { getAssignmentRecipe } from './mealPlanHelpers';

export const printShoppingList = (mealPlan: MealPlan, groceryList: GroceryItem[]) => {
  if (!mealPlan) return;
  
  // Filter out checked items for printing
  const itemsToShow = groceryList.filter(item => !item.alreadyHave);
  
  // Add additional items from meal plan if they exist
  const additionalItemsToShow: GroceryItem[] = [];
  if (mealPlan.additionalItems && mealPlan.additionalItems.length > 0) {
    mealPlan.additionalItems.forEach(itemName => {
      additionalItemsToShow.push({
        itemName,
        totalQuantity: 0,
        unit: '',
        productSearchTerms: [],
        usedInRecipes: [],
        optional: false,
        category: 'additional_items',
        isAdditionalItem: true
      });
    });
  }
  
  // Combine grocery list items with additional items
  const allItemsToShow = [...itemsToShow, ...additionalItemsToShow];
  
  // If there are no items to show, don't print
  if (allItemsToShow.length === 0) return;
  
  // Categorize items
  const categorizeItem = (item: GroceryItem): string => {
    return item.category || 'other';
  };
  
  const categoryDisplayNames: Record<string, string> = {
    produce: 'Produce',
    meat: 'Meat & seafood',
    dairy: 'Dairy & eggs',
    pantry_dry_goods: 'Pantry & dry goods',
    spices_seasonings: 'Spices & seasonings',
    frozen: 'Frozen',
    other: 'Other',
    additional_items: 'Additional items'
  };
  
  const categorizedItems = allItemsToShow.reduce((acc, item) => {
    const category = categorizeItem(item);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, GroceryItem[]>);
  
  // Sort items within each category
  Object.keys(categorizedItems).forEach(category => {
    categorizedItems[category].sort((a, b) => 
      a.itemName.toLowerCase().localeCompare(b.itemName.toLowerCase())
    );
  });
  
  const categoryOrder = ['produce', 'meat', 'dairy', 'pantry_dry_goods', 'spices_seasonings', 'frozen', 'other', 'additional_items'];
  const sortedCategories = categoryOrder.filter(cat => categorizedItems[cat]?.length > 0);
  
  // Generate HTML content
  let htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Shopping List - Week of ${formatDateRange(mealPlan)}</title>
        <meta charset="utf-8">
        <style>
          @page {
            margin: 0.75in;
          }
          body {
            font-family: Arial, sans-serif;
            line-height: 1.4;
            color: #000;
            background: white;
            font-size: 12pt;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .header h1 {
            margin: 0 0 8px 0;
            font-size: 18pt;
            font-weight: bold;
          }
          .header .meal-plan-name {
            font-weight: bold;
            font-size: 14pt;
            margin-bottom: 4px;
          }
          .category {
            margin-bottom: 25px;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .category h2 {
            font-size: 14pt;
            font-weight: bold;
            text-transform: uppercase;
            border-bottom: 1px solid #999;
            margin: 0 0 10px 0;
            padding-bottom: 4px;
          }
          .items-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-bottom: 15px;
          }
          .item {
            display: flex;
            align-items: flex-start;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .checkbox {
            width: 14px;
            height: 14px;
            border: 1.5pt solid #000;
            margin-right: 10px;
            margin-top: 2px;
            flex-shrink: 0;
          }
          .item-details {
            flex: 1;
          }
          .item-name {
            font-weight: 500;
            font-size: 11pt;
          }
          .item-quantity {
            color: #666;
            font-weight: normal;
          }
          .item-preference {
            font-style: italic;
            color: #666;
            font-size: 10pt;
            margin-top: 2px;
          }
          .item-recipes {
            font-size: 9pt;
            color: #666;
            margin-top: 2px;
          }
          .meals-summary {
            margin-top: 40px;
            border-top: 1px solid #ccc;
            padding-top: 20px;
          }
          .meals-summary h3 {
            font-size: 12pt;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .meal-item {
            font-size: 10pt;
            margin-bottom: 4px;
          }
          .meal-day {
            font-weight: bold;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 9pt;
            color: #666;
            border-top: 1px solid #eee;
            padding-top: 15px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Shopping List</h1>
          <div class="meal-plan-name">Week of ${formatDateRange(mealPlan)}</div>
        </div>
  `;
  
  // Add categorized items
  sortedCategories.forEach(category => {
    const items = categorizedItems[category];
    if (!items || items.length === 0) return;
    
    htmlContent += `
      <div class="category">
        <h2>${categoryDisplayNames[category] || category.replace('_', ' ')}</h2>
        <div class="items-grid">
    `;
    
    items.forEach(item => {
      const formattedQuantity = item.isAdditionalItem ? '' : formatQuantity(item.totalQuantity, item.unit);
      
      htmlContent += `
          <div class="item">
            <div class="checkbox"></div>
            <div class="item-details">
              <div class="item-name">
                ${item.itemName}
                ${formattedQuantity ? `<span class="item-quantity"> (${formattedQuantity})</span>` : ''}
                ${item.optional ? '<span class="item-quantity"> (optional)</span>' : ''}
              </div>
              ${item.productPreference ? `<div class="item-preference">${item.productPreference}</div>` : ''}
            </div>
          </div>
      `;
    });
    
    htmlContent += '</div></div>';
  });
  
  // Add meals summary
  if (mealPlan.assignments && mealPlan.assignments.length > 0) {
    htmlContent += `
      <div class="meals-summary">
        <h3>This Week's Meals:</h3>
    `;
    
    // Chronological within each day (breakfast, lunch, dinner). Slots are
    // labeled all-or-nothing: once the plan holds any breakfast/lunch, dinner
    // rows say "dinner" too, so a bare "Monday:" never sits beside labeled
    // rows reading like a mystery third meal. Dinner-only plans keep the
    // plain day names.
    const slotOrder = { breakfast: 0, lunch: 1, dinner: 2 } as const;
    const labelSlots = mealPlan.assignments.some(
      a => getAssignmentMealType(a) !== 'dinner'
    );
    [...mealPlan.assignments]
      .sort(
        (a, b) =>
          new Date(a.plannedDate).getTime() - new Date(b.plannedDate).getTime() ||
          slotOrder[getAssignmentMealType(a)] - slotOrder[getAssignmentMealType(b)]
      )
      .forEach(assignment => {
        const recipe = getAssignmentRecipe(assignment, mealPlan);
        const dayLabel = labelSlots
          ? `${assignment.plannedDay} ${getAssignmentMealType(assignment)}`
          : assignment.plannedDay;
        if (recipe) {
          htmlContent += `
            <div class="meal-item">
              <span class="meal-day">${dayLabel}:</span> ${recipe.name}
            </div>
          `;
        }
      });
    
    htmlContent += '</div>';
  }
  
  // Add footer
  htmlContent += `
        <div class="footer">
          Generated by Meals You Love • ${new Date().toLocaleDateString()}
        </div>
      </body>
    </html>
  `;
  
  // Create a blob and object URL for the HTML content
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  
  // Open print window with the blob URL
  const printWindow = window.open(url, '_blank');
  if (printWindow) {
    // Wait for content to load, then print
    printWindow.addEventListener('load', () => {
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        // Clean up the object URL after printing
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
      }, 250);
    });
  }
};